import React, { useEffect, useRef, useState } from "react";

// MODULES
import * as d3 from "d3";

// STYLES
import "./ReportHRGraph.scss";

export type ReportHRGraphData = {
    high: number;
    low: number;
    date: string;
};

interface IReportHRGraphProps {
    className?: string;
    data: ReportHRGraphData[];
    containerWidth: number;
    inPrintMode?: boolean;
    //pixelsPerSecond?: number; // not to be confused with sampling - this is literally the length of a second on the screen
    chartHeight?: number;
}

const ReportHRGraph: React.FC<IReportHRGraphProps> = ({
    className,
    data,
    containerWidth,
    inPrintMode,
    //pixelsPerSecond,
    chartHeight, // TODO - right now defaulting to 300
}) => {
    //const [widthToUse, setWidthToUse] = useState<number>(containerWidth);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // useEffect(() => {
    //     console.log("in print mode: ", inPrintMode, containerWidth);
    //     if (!inPrintMode) {
    //         setWidthToUse(containerWidth);
    //     } else {
    //         setWidthToUse(700);
    //     }
    // }, [inPrintMode, containerWidth]);

    useEffect(() => {
        if (!data.length || !containerRef.current /*|| !widthToUs*/) return;
        const effectiveWidth = inPrintMode ? 750 : containerWidth;

        const container = d3.select(containerRef.current);
        container.selectAll("*").remove();

        //const margin = { top: 20, right: 20, bottom: 40, left: 40 };
        const margin = { top: 20, right: 10, bottom: 60, left: 35 }; // updated margins

        const maxBarWidth = 25;
        const maxGap = 20;
        const minBarWidth = 3;
        const minGap = 7;

        let barWidth = maxBarWidth;
        let gap = maxGap;

        const availableWidth = effectiveWidth - margin.left - margin.right;

        if (effectiveWidth > 600) {
            const totalMinWidth = data.length * (minBarWidth + minGap) - minGap;
            const totalMaxWidth = data.length * (maxBarWidth + maxGap) - maxGap;

            if (totalMaxWidth <= availableWidth) {
                // use max values TODO - this can be tweaked more to prevent wrapping
                barWidth = maxBarWidth;
                gap = maxGap;
            } else if (totalMinWidth <= availableWidth) {
                const idealSpacePerElement = availableWidth / data.length;
                // NOTE - not using porportional scaling this just splits remaining space directly
                gap = Math.max(
                    minGap,
                    Math.min(maxGap, idealSpacePerElement * 0.3),
                );
                barWidth = Math.max(
                    minBarWidth,
                    Math.min(maxBarWidth, idealSpacePerElement - gap),
                );
            } else {
                // clamp to min regardless of if min bar+gap exceeds available space
                barWidth = minBarWidth;
                gap = minGap;
            }
        } else {
            // fallback to flexible scaling
            const spacePerElement = availableWidth / (data.length || 1);
            barWidth = Math.min(
                Math.max(spacePerElement * 0.6, minBarWidth),
                maxBarWidth,
            );
            gap = Math.min(Math.max(spacePerElement * 0.4, minGap), maxGap);
        }

        const daysPerRow = Math.floor(
            (effectiveWidth - margin.left - margin.right) / (barWidth + gap),
        );
        const numRows = Math.ceil(data.length / daysPerRow);
        const chartHeightToUse = chartHeight ?? 300; // TODO
        const fullHeight = chartHeightToUse * numRows;

        const visibleData = data.filter((d) => !(d.high === 0 && d.low === 0));
        const maxHigh = d3.max(visibleData, (d) => d.high) ?? 0;
        const minLow = d3.min(visibleData, (d) => d.low) ?? 0;
        //const yMax = Math.ceil((maxHigh + 5) / 5) * 5;
        const yMax = Math.ceil((maxHigh + 5 - 0.001) / 5) * 5;
        const yMin = Math.floor((minLow - 5) / 5) * 5;

        const xLabelGap = 5; // show every nth x-axis label to show

        const svg = container
            .append("svg")
            .attr("width", effectiveWidth)
            .attr("height", fullHeight);

        const yScale = d3
            .scaleLinear()
            .domain([yMin, yMax])
            .range([chartHeightToUse - margin.bottom, margin.top]);

        // Not to be confused with d3 rows, this is actual layout rows (i.e. num rows === number of stacked graphs)
        for (let row = 0; row < numRows; row++) {
            const startIdx = row * daysPerRow;
            const endIdx = Math.min(startIdx + daysPerRow, data.length);
            const rowData = data.slice(startIdx, endIdx);

            const yOffset = row * chartHeightToUse;

            // x-axis scale and ticks
            const xScale = d3
                .scaleBand()
                .domain(rowData.map((_, i) => i.toString()))
                .range([
                    margin.left - margin.left / 10,
                    margin.left + rowData.length * (barWidth + gap) - gap,
                ])
                .padding(0.1);

            // calcs where to terminate the horizontal bars
            const lastXBand = xScale((rowData.length - 1).toString());
            const xAxisRightEdge =
                lastXBand !== undefined
                    ? lastXBand + xScale.bandwidth()
                    : effectiveWidth - margin.right;

            // y-axis
            const yAxis = d3.axisLeft(yScale).ticks(5);
            svg.append("g")
                .attr("transform", `translate(${margin.left - 5}, ${yOffset})`)
                .call(yAxis);

            const yTicks = yScale.ticks(5);
            yTicks.forEach((tickValue) => {
                const y = yScale(tickValue);
                svg.append("line")
                    .attr("x1", margin.left)
                    .attr("x2", xAxisRightEdge) //readjusted endpoint
                    .attr("y1", y + yOffset)
                    .attr("y2", y + yOffset)
                    .attr("stroke", "#ccc")
                    .attr("stroke-width", 1);
                //.attr("shape-rendering", "crispEdges");
            });

            // x-axis
            svg.append("g")
                .attr(
                    "transform",
                    `translate(0, ${
                        // REMINDER - this is confusing, but it effectively draws the first x-axis at 0,0 and
                        // the second at (0, chart height + bottom margin), and so on.
                        yOffset + chartHeightToUse - margin.bottom
                    })`,
                )
                .call(
                    d3
                        .axisBottom(xScale)
                        .tickFormat((_, i) =>
                            // d3.timeFormat("%-m/%-d/%y")(
                            //     new Date(rowData[i].date),
                            // ),
                            i % xLabelGap === 0
                                ? d3.timeFormat("%-m/%-d/%y")(
                                      new Date(rowData[i].date),
                                  )
                                : "",
                        )
                        .tickSizeOuter(0),
                )
                .selectAll("path, line")
                .attr("stroke", "#000")
                .attr("stroke-width", 1);

            // draw bars and vertical lines aligned to ticks
            rowData.forEach((d, i) => {
                const x = xScale(i.toString());
                if (x === undefined) return;

                let yHigh = yScale(d.high);
                let yLow = yScale(d.low);
                //let barHeight = yLow - yHigh;

                // If high === low and not zero, force a visible bar height (2px for now)
                if (d.high === d.low && d.high !== 0) {
                    const barScale = 10;
                    yHigh -= barScale; // extend x px upward
                    //barHeight = barScale * 2;
                }

                const group = svg
                    .append("g")
                    .attr("transform", `translate(0, ${yOffset})`);

                // vertical lines
                group
                    .append("line")
                    .attr("x1", x + xScale.bandwidth() / 2)
                    .attr("x2", x + xScale.bandwidth() / 2)
                    .attr("y1", margin.top)
                    .attr("y2", chartHeightToUse - margin.bottom)
                    .attr("stroke", "#ccc")
                    .attr("stroke-width", 1);

                // bars
                group
                    .append("rect")
                    .attr("x", x)
                    .attr("y", yHigh)
                    .attr("width", xScale.bandwidth())
                    .attr("height", Math.max(yLow - yHigh, 0))
                    .attr("fill", "#59A6C4") // Match scss var
                    .attr("rx", 4);
            });
        }
    }, [data, containerWidth, chartHeight]);

    return <div ref={containerRef} className={`ReportHRGraph`}></div>;
};

export default ReportHRGraph;
